import { ChangeEvent, MouseEvent, useCallback, useState } from 'react';

import {
  Box,
  Card,
  Container,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  useTable,
} from 'src/components/table';

import { paths } from 'src/routes/paths';

import { useListInboundEmailLogsQuery } from 'src/services/inbound-email/inbound-email.service';
import {
  IInboundEmailLog,
  InboundEmailLogStatus,
} from 'src/services/inbound-email/inbound-email.types';

import InboundEmailTableRow from '../components/inbound-email-table-row';
import ManuallyAssignModal from '../components/manually-assign-modal';
import ViewParsedDataModal from '../components/view-parsed-data-modal';

const TABLE_HEAD = [
  { id: 'created_at', label: 'Received' },
  { id: 'from_email', label: 'From' },
  { id: 'subject', label: 'Subject' },
  { id: 'status', label: 'Status' },
  { id: 'source_board', label: 'Board' },
  { id: 'candidate', label: 'Candidate' },
  { id: 'actions', label: '', align: 'right' as const },
];

const SOURCE_BOARDS = ['seek', 'linkedin', 'indeed', 'other'];

export default function InboundEmailActivityView({ embedded = false }: { embedded?: boolean }) {
  const settings = useSettingsContext();
  const authContext = useAuthContext();
  const orgId = authContext.organization.id;

  const table = useTable({ defaultRowsPerPage: 20 });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<InboundEmailLogStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const [viewing, setViewing] = useState<IInboundEmailLog | null>(null);
  const [assigning, setAssigning] = useState<IInboundEmailLog | null>(null);

  const { currentData: logs, isFetching } = useListInboundEmailLogsQuery({
    organizationId: orgId,
    page,
    per_page: perPage,
    status: statusFilter || undefined,
    source_board: sourceFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const onChangePage = useCallback(
    (_event: MouseEvent<HTMLButtonElement, MouseEvent>, pageNumber: number) => setPage(pageNumber + 1),
    []
  );

  const onChangePageRows = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPerPage(+(event.target as HTMLInputElement).value);
      setPage(1);
    },
    []
  );

  const notFound = !isFetching && !logs?.count;

  return (
    <Container maxWidth={embedded || settings.themeStretch ? false : 'xl'} disableGutters={embedded}>
      {!embedded && (
        <CustomBreadcrumbs
          heading="Inbound email activity"
          links={[
            { name: 'Dashboard', href: paths.dashboard.root },
            { name: 'Admin' },
            { name: 'Inbound email activity' },
          ]}
          sx={{ mb: { xs: 3, md: 5 } }}
        />
      )}

      <Card>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2, alignItems: { md: 'center' } }}
        >
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as InboundEmailLogStatus | ''); setPage(1); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {Object.values(InboundEmailLogStatus).map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                {s.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Board"
            size="small"
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All boards</MenuItem>
            {SOURCE_BOARDS.map((b) => (
              <MenuItem key={b} value={b} sx={{ textTransform: 'capitalize' }}>{b}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            sx={{ minWidth: 160 }}
          />

          {(statusFilter || sourceFilter || dateFrom || dateTo) && (
            <Box
              component="button"
              type="button"
              onClick={() => { setStatusFilter(''); setSourceFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              sx={{
                background: 'none', border: 0, cursor: 'pointer', color: 'primary.main',
                fontSize: 14, textDecoration: 'underline',
              }}
            >
              Clear filters
            </Box>
          )}
        </Stack>

        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'}>
              <TableHeadCustom
                order={table.order}
                headLabel={TABLE_HEAD}
                rowCount={logs?.count ?? 0}
                numSelected={table.selected.length}
              />
              <TableBody>
                {logs?.results.map((row) => (
                  <InboundEmailTableRow
                    key={row.id}
                    row={row}
                    onView={(r) => setViewing(r)}
                    onAssign={(r) => setAssigning(r)}
                  />
                ))}

                <TableEmptyRows
                  height={table.dense ? 56 : 76}
                  emptyRows={emptyRows(page - 1, perPage, logs?.count ?? 0)}
                />

                <TableNoData notFound={notFound} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={logs?.count ?? 0}
          page={page}
          rowsPerPage={perPage}
          onPageChange={onChangePage as any}
          onRowsPerPageChange={onChangePageRows}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>

      <ViewParsedDataModal
        open={!!viewing}
        onClose={() => setViewing(null)}
        log={viewing}
      />
      <ManuallyAssignModal
        open={!!assigning}
        onClose={() => setAssigning(null)}
        log={assigning}
      />
    </Container>
  );
}
